// Libs
import React, { useContext, useEffect, useRef, useState } from 'react';
// Context
import { ElementViewBuilderContext } from 'core/context';
// Hooks
import { useToastMessage } from 'hooks';
import useOrderApi from 'core/hooks/useCoreApi';
// Tabs
import {
	BillingTab,
	DetailsTab,
	HistoryTab,
	RestrictionsTab,
	ReviewsTab,
} from './Tabs';
import useBillingMaterials from './Tabs/useBillingMaterials';
// Components
import { CloseIcon, Tabs, Wrapper } from 'components';
import CollectorOrderModel from 'components/Collector/OrderModel';
import Spining from './Spining';
import OrderOverviewLoadError from './OrderOverviewLoadError';
// Styles
import './index.css';

const { Wrap: TabWrap, Pane: TabPane } = Tabs;

function reviewOrderDataMapping({ orderId, orderData, metadata }) {
	const data = {
		orderId,
		serviceId: orderData.fields.find(f => f.id === 'serviceId')?.value,
		departmentId: orderData.fields.find(f => f.id === 'departmentId')?.value,
		odtId: orderData.fields.find(f => f.id === 'odtId')?.value,

		departmentProps: metadata.departmentProps,
		serviceProps: metadata.serviceProps,
		orderProps: metadata.orderProps,
	};
	return new CollectorOrderModel(data);
}

const OrderInformation = ({ orderId }) => {
	const [state, _setState] = useState({
		orderId: undefined,
		isFetching: true,
		data: undefined,
		metadata: undefined,
		error: undefined,
	});
	const setState = newProps =>
		_setState(current => ({ ...current, ...newProps }));
	const [activeTap, setActivatedTap] = useState('detailKey');
	const { payload = {}, cleanElementView } = useContext(
		ElementViewBuilderContext,
	);
	const { getOrderDetails } = useOrderApi();
	const sendToast = useToastMessage();
	const [overviewRefreshKey, setOverviewRefreshKey] = useState(0);
	const [historyRefreshKey, setHistoryRefreshKey] = useState(0);
	const billing = useBillingMaterials(orderId);
	const isMounted = useRef(true);
	const requestGeneration = useRef(0);
	const handleRestrictionChanged = changedOrderId => {
		if (changedOrderId !== orderId) return;
		setOverviewRefreshKey(current => current + 1);
		setHistoryRefreshKey(current => current + 1);
		billing.refresh();
	};
	const requestOverviewReload = shouldReload => {
		if (shouldReload) setOverviewRefreshKey(current => current + 1);
	};
	const retryOverview = () => setOverviewRefreshKey(current => current + 1);

	useEffect(() => {
		if (!orderId) return undefined;
		isMounted.current = true;
		const generation = requestGeneration.current + 1;
		requestGeneration.current = generation;

		setState({
			orderId,
			isFetching: true,
			data: undefined,
			metadata: undefined,
			error: undefined,
		});
		getOrderDetails({
			orderId,
			datasourceAccessId: payload.datasourceAccessId,
			orderOverviewMode: payload.orderOverviewMode,
		})
			.then(({ payload }) => {
				if (!isMounted.current || requestGeneration.current !== generation)
					return;
				if (!payload?.data || !payload?.metadata) {
					sendToast({
						message: 'No se pudo cargar el overview de la orden',
						type: 'warning',
					});
					setState({
						orderId,
						data: undefined,
						metadata: undefined,
						isFetching: false,
						error: {
							message: 'La respuesta del overview de la orden es inválida',
						},
					});
					return;
				}
				setState({
					orderId,
					data: payload.data,
					metadata: payload.metadata,
					isFetching: false,
					error: undefined,
				});
			})
			.catch(error => {
				if (!isMounted.current || requestGeneration.current !== generation)
					return;
				const message = error?.message;
				const type = error?.type;
				if (message) sendToast({ message, type });
				setState({
					orderId,
					isFetching: false,
					error:
						error || new Error('No se pudo cargar el overview de la orden'),
				});
			});
		return () => {
			if (requestGeneration.current === generation)
				requestGeneration.current += 1;
		};
	}, [
		orderId,
		payload.datasourceAccessId,
		payload.orderOverviewMode,
		overviewRefreshKey,
	]);

	useEffect(() => {
		return () => {
			isMounted.current = false;
			requestGeneration.current += 1;
			cleanElementView();
		};
	}, []);

	const currentState =
		state.orderId === orderId
			? state
			: {
					orderId,
					isFetching: true,
					data: undefined,
					metadata: undefined,
					error: undefined,
				};

	return (
		<Wrapper
			mediumBackground
			padding='0 10px'
			width='100%'
			height='100%'
			maxWidth='700px'
			justifyContent='center'
			alignItems='flex-start'
		>
			<TabWrap
				activeKey={activeTap}
				tabPosition='top'
				onChange={setActivatedTap}
			>
				<TabPane tab='Detalle' key='detailKey'>
					{currentState.isFetching ? (
						<Spining />
					) : currentState.error ? (
						<OrderOverviewLoadError
							error={currentState.error}
							onRetry={retryOverview}
						/>
					) : (
						<DetailsTab
							orderId={orderId}
							data={currentState.data}
							metadata={currentState.metadata}
							setReload={requestOverviewReload}
							cleanElementView={cleanElementView}
						/>
					)}
				</TabPane>
				<TabPane tab='Historial' key='historyKey'>
					<HistoryTab orderId={orderId} refreshKey={historyRefreshKey} />
				</TabPane>
				{currentState.data?.order && currentState.metadata && (
					<TabPane tab='Reportería' key='collectorEcosystemKey'>
						<ReviewsTab
							order={reviewOrderDataMapping({
								orderId,
								orderData: currentState.data.order,
								metadata: currentState.metadata,
							})}
						/>
					</TabPane>
				)}
				<TabPane tab='Inventario' key='inventoryKey'>
					<BillingTab
						key={orderId}
						orderId={orderId}
						metadata={currentState.metadata}
						billing={billing}
					/>
				</TabPane>
				<TabPane tab='Restricciones' key='restrictionsKey'>
					<RestrictionsTab
						orderId={orderId}
						isActive={activeTap === 'restrictionsKey'}
						isFetching={currentState.isFetching}
						data={currentState.data}
						metadata={currentState.metadata}
						billing={billing}
						onChanged={handleRestrictionChanged}
					/>
				</TabPane>
			</TabWrap>
			<Wrapper position='absolute' right='0px' padding='0' mediumBackground>
				<CloseIcon button onClick={cleanElementView} />
			</Wrapper>
		</Wrapper>
	);
};
export default OrderInformation;
